import { InvalidScheduledBlocksAssignment } from "@models/ISchema";

// CHS-2785 / EDH-014 (backend code VALIDATE_EXCEEDED_SCHEDULED_BLOCKS, PR
// Foris/darwined#5420). Kept as a named constant (rather than a literal in every
// switch/case) so the single source of truth stays here if it ever changes.
export const SCHEDULED_BLOCKS_ASSIGNMENT_TYPENAME: InvalidScheduledBlocksAssignment["__typename"] =
  "InvalidScheduledBlocksAssignment";

export type ScheduledBlocksDirection = "deficit" | "excess";
export type ScheduledBlocksQuantity = "singular" | "plural";
export type ScheduledBlocksMessageSurface = "summary" | "detail";

export interface ScheduledBlocksAssignmentPayload {
  sectionCode: string;
  planned: number | string;
  assigned: number | string;
  difference: number | string;
  direction: ScheduledBlocksDirection;
  quantity: ScheduledBlocksQuantity;
}

const getSignedDifference = (
  incidence: Partial<InvalidScheduledBlocksAssignment>,
): number | null => {
  if (typeof incidence?.exceededBlocks === "number") return incidence.exceededBlocks;

  if (
    typeof incidence?.assignedBlocks === "number" &&
    typeof incidence?.plannedBlocks === "number"
  ) {
    return incidence.assignedBlocks - incidence.plannedBlocks;
  }

  return null;
};

export const mapScheduledBlocksAssignmentPayload = (
  incidence: Partial<InvalidScheduledBlocksAssignment>,
): ScheduledBlocksAssignmentPayload => {
  const section = incidence?.section;
  // section is optional on the type (defensive: keep working even if a given error
  // instance omits it), so the code prefix stays conditional at render time.
  const componentCode = section?.component?.code ?? section?.courseComponent?.component?.code ?? "";
  const sectionCode = [componentCode, section?.code].filter(Boolean).join(" ");
  const signedDifference = getSignedDifference(incidence);
  const difference = signedDifference == null ? "-" : Math.abs(signedDifference);

  return {
    sectionCode,
    planned: incidence?.plannedBlocks ?? "-",
    assigned: incidence?.assignedBlocks ?? "-",
    difference,
    direction: signedDifference != null && signedDifference < 0 ? "deficit" : "excess",
    quantity: difference === 1 ? "singular" : "plural",
  };
};

export const getScheduledBlocksMessageKey = (
  surface: ScheduledBlocksMessageSurface,
  payload: Pick<ScheduledBlocksAssignmentPayload, "direction" | "quantity">,
): string =>
  `assignation-edit.incidence-table.validations.scheduled-blocks.${surface}.${payload.direction}.${payload.quantity}`;

export const isBlockingScheduledBlocksAssignment = (
  incidence: { __typename?: string | null; allowed?: boolean | null } | null | undefined,
): boolean =>
  incidence?.__typename === SCHEDULED_BLOCKS_ASSIGNMENT_TYPENAME && incidence.allowed === false;

export const selectScheduledBlocksSummaryIncidence = (
  incidences: ReadonlyArray<
    { __typename?: string | null; allowed?: boolean | null } | null | undefined
  >,
): Partial<InvalidScheduledBlocksAssignment> | null => {
  const scheduledBlocksIncidences = incidences.filter(
    incidence => incidence?.__typename === SCHEDULED_BLOCKS_ASSIGNMENT_TYPENAME,
  ) as Partial<InvalidScheduledBlocksAssignment>[];

  return (
    scheduledBlocksIncidences.find(isBlockingScheduledBlocksAssignment) ??
    scheduledBlocksIncidences[0] ??
    null
  );
};
